// @ts-check
import moment from 'moment-timezone';
import { captureSentryMessage } from '../../../shared/errorHandler';
import { absentModalController } from '../absent-modal/absent-modal.ctrl.js';
import absentModalTemplate from '../absent-modal/absent-modal.html';

import dsbCtrl from '../../workforce/rate/dsb.ctrl';
import dsbTpl from '../../workforce/rate/dsb.html';
import editShiftModal from './../edit-shift-modal/edit-shift-modal.html';
import editShiftModalCtrl from './../edit-shift-modal/edit-shift-modal.ctrl.js';
import cancelShiftModal from './../cancel-shift-modal/cancel-shift-modal.html';
import cancelShiftModalCtrl from './../cancel-shift-modal/cancel-shift-modal.ctrl.js';
import groupMessage from "../../workforce/message/groupMessage.html";
import groupMessageController from "../../workforce/message/groupMessage.ctrl";
import {formatCertificationIcons, getCovidPolicyIcon} from "../../../component-templates/certification-icon/utils";

// Segment events
const rosterFavoriteEvent = 'roster_favorite';
const rosterMarkAbsent = 'roster_mark_absent';
const rosterDsb = 'roster_dsb';
const rosterViewProfile = 'roster_view_profile';

class ShiftDetailsController {
  /* @ngInject */
  constructor (
    $scope,
    $window,
    $mdDialog,
    mdPanelRef,
    $mdToast,
    Jobs,
    Shifts,
    Users,
    jobs,
    selectedShift,
    selectedUser,
    segment,
    clock,
    undoHours,
    updateRosterProps,
    userChatFn,
    reactAppUrl,
    getCompanyJobs,
    shiftJob,
    userCanPostJobs,
    rosterUserCertificationService,
    ) {
    this.$scope = $scope;
    this.$window = $window;
    this.$mdDialog = $mdDialog;
    this.$mdToast = $mdToast;
    this.mdPanelRef = mdPanelRef;
    this.Jobs = Jobs;
    this.Users = Users;
    this.Shifts = Shifts;
    this.shiftJob = shiftJob;
    this.selectedShift = selectedShift;
    this.selectedUser = selectedUser;
    this.segment = segment;
    this.jobs = jobs;
    this.selectedShiftJob = this.jobs.filter(job => job.id === this.selectedShift.job_id);
    this.undoHours = undoHours;
    this.updateRosterProps = updateRosterProps;
    this.userChatFn = userChatFn;
    this.reactAppUrl = reactAppUrl;
    this.clock = clock;
    this.getCompanyJobs = getCompanyJobs;
    this.isClockedIn = this.selectedShift.timestamps && this.selectedShift.timestamps.length && this.selectedShift.category !== 'Clocked Out';
    this.isClockedOut = !this.selectedShift.timestamps || this.selectedShift.category === 'Clocked Out';
    this.isFavorite = this.selectedUser[0].is_favorite;
    this.userCanPostJobs = userCanPostJobs;
    this.clockInOutEnabled = this.getClockInOutEnabled(this.selectedShift.start_time);
    this.pastShift = this.getPastShift(this.selectedShift.start_time);
    this.shiftStatus = this.isClockedIn ? 'Clocked in' : 'Not clocked in';
    // Setting job object on the 'roster/shift' obj
    this.selectedShift.job = this.selectedShiftJob[0];
    this.closeDialog = () => this.mdPanelRef.close();
    this.isUserDontSendBack = this.selectedUser[0].dont_send_back;

    const shiftStartDate = moment(this.selectedShift.job.start_date).tz(this.selectedShift.timezone).format('MMM D');
    const shiftEndDate = moment(this.selectedShift.job.end_date).tz(this.selectedShift.timezone).format('MMM D');
    this.formattedShiftDate = `${shiftStartDate} - ${shiftEndDate}`;
    const shiftStartTime = moment(this.selectedShift.job.start_date).tz(this.selectedShift.timezone).format('h:mm a');
    const shiftEndTime = moment(this.selectedShift.job.end_date).tz(this.selectedShift.timezone).format('h:mm a');
    this.formattedShiftTime = `${shiftStartTime} – ${shiftEndTime}`;
    this.assignmentEndDate = moment.tz(this.selectedShift.job.end_date, this.selectedShift.timezone).format('M/d');
    this.rosterUserCertificationService = rosterUserCertificationService;
    this.fetchUserCertifications();
  }

  /**
   * This function fetches the user's certifications and sets them in the component's state to be displayed as icons.
   */
  fetchUserCertifications() {
    const covidPolicyIcon = getCovidPolicyIcon(this.selectedShift);
    this.rosterUserCertificationService.getUserCertifications(this.selectedUser[0].external_id).then((certs) => {
      this.userCertificationIcons = [
        ...(covidPolicyIcon.has_icon ? [covidPolicyIcon] : []),
        ...formatCertificationIcons(certs),
      ]
    });
  }

  openEditShiftModal = () => {
    if (this.pastShift) return;
    this.closeDialog();
    const parentEl = document.body;
    return this.$mdDialog.show({
      parent: parentEl,
      fullscreen: true,
      template: editShiftModal,
      locals: {
        shiftInfo: this.selectedShift,
        selectedUser: this.selectedUser,
        getCompanyJobs: this.getCompanyJobs
      },
      controller: editShiftModalCtrl,
      controllerAs: 'e',
    });
  }

  viewProfileRedirect = (userId) => {
    this.closeDialog()
    // Segment Event Tracking
    this.segment.track(rosterViewProfile, {
      userId
    })

    this.$window.location.href = `/app.html#!/resume/${userId}`
  }

  getPastShift = (shiftStartTime) => {
    const shiftStartDay = moment.tz(shiftStartTime, this.selectedShift.timezone);
    const today = moment().tz(this.selectedShift.timezone);
    return shiftStartDay.isBefore(today);
  }

  getClockInOutEnabled = (shiftStartTime) => {
    // We should be able to Clock In/Out only shifts that are started yesterday or today
    const today = moment().tz(this.selectedShift.timezone);
    const yesterday = moment(today).tz(this.selectedShift.timezone).add(-1, 'days');
    const shiftStartDay = moment(shiftStartTime).tz(this.selectedShift.timezone);

    const isToday = shiftStartDay.isSame(today, "day");
    const isYesterday = shiftStartDay.isSame(yesterday, "day");
    return isToday || isYesterday;
  }

  handleFavorite = (shift) => {
    if (this.isFavorite) {
      this.unfavorite(shift)
    } else {
      this.favorite(shift)
    }
  }

  userChat = (workerId) => {
    this.closeDialog()
    this.userChatFn(workerId)
  }

  favorite = (shift) => {
    const user = this.selectedUser[0]

    // Segment event for tracking favorite action
    this.segment.track(rosterFavoriteEvent, {
      jobId: shift.job.id,
      scheduleId: shift.schedule_id,
      shiftId: shift.id
    })

    this.Users.favorite([user.external_id], shift.job.external_id)
      .then((res) => {
        this.updateRosterProps(user, { is_favorite: true })
        this.isFavorite = true
        const msg = `${user.fullname} added as a favorite!`
        this.$mdToast.showSimple(msg)
      })
      .catch(error => console.log('favorite - error', error));
  }

  unfavorite = (shift) => {
    const user = this.selectedUser[0]

    this.Users.unfavorite([user.external_id], shift.job.external_id)
      .then(() => {
        this.updateRosterProps(user, { is_favorite: false });
        this.isFavorite = false;
        const msg = `${user.fullname} removed as favorite`;
        this.$mdToast.showSimple(msg);
      })
      .catch(error => console.log('unfavorite - error', error));
  }

  clockInOut = () => {
    const isClockedIn = this.selectedShift.category === 'Clocked-In'
    let type

    if (isClockedIn) {
      type = 'SHIFT_END'
    } else {
      type = 'SHIFT_START'
    }

    this.clock(type, this.selectedShift)

    // Segment Event Tracking
    this.segment.track('roster_clock_in_out', {
      job_id: this.selectedShift.job_id,
      scheduleId: this.selectedShift.schedule_id,
      shift: this.selectedShift.shift
    });

    this.closeDialog()
  }

  dontSendError = (err) => {
    this.$scope.$broadcast('app:loading:done')
    if (!err) return
    console.error(err)
    this.$mdToast.showSimple('Unable to mark user as don\'t send.')
  }

  dsb = ($ev) => {
    const roster = this.selectedShift;
    this.closeDialog();

    if (roster.job) roster.job.timezone = roster.timezone;
    const confirm = {
      controller: dsbCtrl,
      controllerAs: 'dsb',
      template: dsbTpl,
      fullscreen: true,
      targetEvent: $ev,
      clickOutsideToClose: true,
      locals: {
        worker: roster.worker,
        job: roster.job,
        schedule: roster.schedule_name || null, // gets filled in below if roster.schedule_id exists
        ratingType: null
      }
    }
    let fetchScheduleDetails = Promise.resolve();
    let dsbResults;
    return fetchScheduleDetails.then(() => this.$mdDialog.show(confirm))
      .then((results) => {
        dsbResults = results
        if (!dsbResults.endDate) return this.Users.dontSend(roster.external_id, dsbResults.reason, dsbResults.dsbRating, dsbResults.categoryId, roster.job.external_id)
        this.Jobs.repost({
          address_id: roster.address_id,
          days: results.days,
          daysOfWeek: roster.days,
          // TODO: revert value back to roster.department once Roster is using Kernel GQL query
          department: roster.department_id,
          dsb: 1,
          endDate: results.endDate,
          jobId: roster.job.external_id,
          needed: results.needed,
          position_id: roster.position_id,
          startDate: results.startDate,
          supervisor: roster.supervisor_id,
          timezone: results.timezone,
          // TODO: revert value back to roster.base_wage once Roster is using Kernel GQL query
          wage: roster.wage
        }).then(this.Users.dontSend(roster.external_id, dsbResults.reason, dsbResults.dsbRating, dsbResults.categoryId, roster.job.external_id))
      })
      .then(this.Users.removeWorker(roster.external_id))
      .then(this.Users.getWorkforce())
      .then(() => {
        roster.dont_send_back = true
        this.$mdToast.showSimple(`Marked ${roster.fullname} as don\'t send.`)
        this.segment.track(rosterDsb, {
          job_id: roster.job_id,
          scheduleId: roster.schedule_id,
          shift: roster.shift
        })
      })
      .catch(this.dontSendError.bind(this))
  }

  /**
   *
   * @param {object} $ev - target event
   * @param {boolean} shiftInProgress - boolean flag to determine whether to only show the in progress shift options
   */
  openCancelModal = ($ev, shiftInProgress) => {
    this.closeDialog();
    
    let shiftJobForModal = this.shiftJob;
    
    if (!shiftJobForModal) {
      const jobFromRoster = this.jobs.find(job => job.id === this.selectedShift.job_id);
      
      if (jobFromRoster) {
        shiftJobForModal = {
          jobId: jobFromRoster.id,
          jobShiftsCount: 0,
        };

        const jobId = jobFromRoster.id;
        
        if (this.selectedUser[0].shifts) {
          shiftJobForModal.jobShiftsCount = this.selectedUser[0].shifts.filter(
            shift => shift.job_id === jobId
          ).length;
        } else {
          captureSentryMessage(
            `[SHIFT DETAILS] Missing shifts array for user - jobId: ${this.selectedShift.job_id}, userId: ${this.selectedUser[0].id}`,
            'warning'
          );
        }
      } else {
        captureSentryMessage(
          `[SHIFT DETAILS] Job not found in roster - jobId: ${this.selectedShift.job_id}, userId: ${this.selectedUser[0].id}`,
          'error'
        );
      }
    }
    
    const cancelModal = {
      controller: cancelShiftModalCtrl,
      controllerAs: 'c',
      template: cancelShiftModal,
      fullscreen: true,
      targetEvent: $ev,
      clickOutsideToClose: true,
      locals: {
        selectedShift: this.selectedShift,
        selectedUser: this.selectedUser,
        shiftJob: shiftJobForModal,
        shiftInProgress,
      },
    };

    this.$mdDialog.show(cancelModal);
  }

  markAbsent = ($ev) => {
    this.closeDialog();
    const roster = this.selectedShift
    const confirm = {
      controller: absentModalController,
      controllerAs: 'm',
      template: absentModalTemplate,
      fullscreen: true,
      targetEvent: $ev,
      clickOutsideToClose: true,
      locals: {
        roster: roster,
        job: roster.job,
      }
    }

    return this.$mdDialog.show(confirm)
    .then((res) => {
      this.$mdToast.showSimple(`Marked ${roster.fullname} absent`)
      this.updateRosterProps(res.roster, { category: res.category, usersHoursReasonCode: res.usersHoursReasonCode })
      // Segment Event Tracking
      this.segment.track(rosterMarkAbsent, {
        jobId: roster.job_id,
        scheudleId: roster.schedule_id,
        shift: roster.shift
      })
      if (res.dsb) {
        this.dsb($ev)
      }
    })
  }

  showGroupMessage = ($ev) =>  {
    this.closeDialog();

    this.$mdDialog.show({
      multiple: true,
      targetEvent: $ev,
      template: groupMessage,
      clickOutsideToClose: true,
      locals: {
        users: [this.selectedUser[0].external_id]
      },
      controller: groupMessageController,
      controllerAs: 'gm'
    })
  }

  undoMarkAbsent = () => {
    this.closeDialog()
    this.undoHours(this.selectedShift.hours_id, this.selectedShift)
  }
}

export default ShiftDetailsController;
